/* Formatting for operator readouts.
 *
 * DESIGN-SYSTEM.md §3: every number that changes in place is tabular, and every
 * unit is labelled. An unlabelled altitude is a hazard, not a style choice.
 */

/** Decimal degrees → degrees/decimal-minutes, the form operators actually read. */
export function formatLatLon(lat: number, lon: number): string {
  return `${dm(Math.abs(lat), lat >= 0 ? 'N' : 'S', 2)}  ${dm(Math.abs(lon), lon >= 0 ? 'E' : 'W', 3)}`
}

function dm(v: number, hemi: string, degPad: number): string {
  const deg = Math.floor(v)
  const min = (v - deg) * 60
  return `${String(deg).padStart(degPad, '0')}°${min.toFixed(2).padStart(5, '0')}'${hemi}`
}

/**
 * Approximate MGRS. Real MGRS comes from GeographicLib in production
 * (GEOSPATIAL-DATA-SOURCES.md §7) — this is display-plausible only.
 */
export function approxMgrs(lat: number, lon: number): string {
  const zone = Math.floor((lon + 180) / 6) + 1
  const bands = 'CDEFGHJKLMNPQRSTUVWX'
  const band = bands[Math.floor((lat + 80) / 8)] ?? 'U'
  const e = Math.floor((((lon % 6) + 6) % 6) * 18500) % 100000
  const n = Math.floor((((lat % 1) + 1) % 1) * 111000) % 100000
  const sq = ['UB', 'VB', 'UC', 'VC', 'PF', 'NF'][zone % 6] ?? 'UB'
  return `${zone}${band} ${sq} ${String(e).padStart(5, '0')} ${String(n).padStart(5, '0')}`
}

export function bearing(deg: number): string {
  return `${String(Math.round(deg)).padStart(3, '0')}°`
}

export function nm(v: number, digits = 1): string {
  return v.toFixed(digits)
}

/** Thousands separators — `number-formatting` guideline. */
export function thousands(v: number): string {
  return v.toLocaleString('en-US')
}

/** Seconds → HH:MM:SS, always padded so the readout never shifts width. */
export function hms(totalSec: number): string {
  const s = Math.max(0, Math.floor(totalSec))
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`
}

/**
 * Sim clock — always Zulu, always with the date.
 * UI-UX-DESIGN.md §7: in a 12-hour exercise crossing midnight, a time without a
 * date causes real errors.
 */
export function simClock(epochIso: string, elapsedSec: number): { date: string; time: string } {
  const t = new Date(new Date(epochIso).getTime() + elapsedSec * 1000)
  const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC']
  const date = `${String(t.getUTCDate()).padStart(2, '0')} ${months[t.getUTCMonth()]} ${t.getUTCFullYear()}`
  const time =
    `${String(t.getUTCHours()).padStart(2, '0')}:` +
    `${String(t.getUTCMinutes()).padStart(2, '0')}:` +
    `${String(t.getUTCSeconds()).padStart(2, '0')}Z`
  return { date, time }
}

/** Contact age → human string plus a staleness tier that drives the visual treatment. */
export function contactAge(sec: number): { text: string; tier: 'fresh' | 'aging' | 'stale' } {
  const tier = sec < 15 ? 'fresh' : sec < 90 ? 'aging' : 'stale'
  if (sec < 60) return { text: `${sec}s`, tier }
  const m = Math.floor(sec / 60)
  return { text: `${m}m ${sec % 60}s`, tier }
}
